import { LoadingButton } from "@mui/lab";
import type { ButtonProps } from "@mui/material";
import { Button, Tooltip } from "@mui/material";
import type { SxProps, Theme } from "@mui/material/styles";
import { styled } from "@mui/material/styles";
import { ROLES } from "containers/ScopeContext/roles.enum";
import useUserRole from "hooks/useUserRole";
import type { CSSProperties, ReactNode } from "react";

import colors from "../../../assets/styles/_variables.module.scss";
import variables from "../../../assets/styles/_variables.module.scss";

// we should take some time one day to find a better way to handle this...
const SButton = styled((props: ButtonProps & { loading?: boolean }) => {
    const { loading, ...buttonProps } = props;
    return loading ? <LoadingButton {...buttonProps} loading={loading} /> : <Button {...buttonProps} />;
})(({ theme, disabled, size, loading }) => ({
    "&.MuiButton-root": {
        backgroundColor: "white",
        border: `2px solid ${colors["border-grey"]}`,
        color: "black",
        borderRadius: 0,
        whiteSpace: "nowrap",
        textTransform: "none",
        "&:hover": {
            backgroundColor: colors["border-grey"],
        },
        "&.Mui-disabled": {
            color: "grey",
            backgroundColor: "white",
        },
        fontFamily: "Poppins, sans-serif",
        fontWeight: "400",
        gap: "8px",
        padding: size == "small" ? "2px 16px" : "8px 16px",
        ...(disabled || loading
            ? {
                  backgroundColor: "#eaeaea !important",
                  color: loading ? "transparent !important" : "#00000040 !important",
              }
            : {}),
    },
}));

export type StyledButtonType = "primary" | "secondary" | "outlined" | "danger" | "info" | "purple";

export default function StyledButton(
    props: Partial<ButtonProps> & {
        buttonType?: StyledButtonType;
        loading?: boolean;
        /**
         * Add sticky-shadow classname
         */
        stickyShadow?: boolean;
        tooltipText?: string | ReactNode;
        allowViewOnly?: boolean;
    },
) {
    const { userRole } = useUserRole();
    if (userRole === ROLES.VIEW_ONLY && !props.allowViewOnly) return <></>;

    const { buttonType, sx = {}, stickyShadow, style, tooltipText = "", ...rest } = props;

    // Adding tooltip and wrapper only when tooltip needed as this breaks css a little.
    // This way we reduce the amount of changes to be made to homogenise.
    // Tried with span/forwardRefs, but none of it worked as well as div.
    return tooltipText ? (
        <Tooltip title={tooltipText} arrow>
            <div>
                <SButton
                    {...rest}
                    style={props.stickyShadow ? stickyShadowStyles : props.style}
                    sx={mergeSx(getButtonTypeStyles(props), sx)}
                />
            </div>
        </Tooltip>
    ) : (
        <SButton
            {...rest}
            style={props.stickyShadow ? stickyShadowStyles : props.style}
            sx={mergeSx(getButtonTypeStyles(props), sx)}
        />
    );
}

export const stickyShadowStyles: CSSProperties = {
    marginTop: "1rem",
    position: "sticky",
    bottom: "0px",
    boxShadow: "0px 0px 20px 0px rgba(0, 0, 0, 0.4)",
};

export const blackButtonStyles = {
    "&.MuiButton-root": {
        borderRadius: variables["border-radius"],
        backgroundColor: "#303030",
        border: "none",
        color: "white",
        height: "1.9rem",
        padding: "2px 16px",
        "&:hover": {
            backgroundColor: "#464646",
        },
    },
};
export const whiteButtonStyles = {
    "&.MuiButton-root": {
        color: "black",
        backgroundColor: "white",
        border: `1px solid ${variables["border-grey"]}`,
        borderRadius: variables["border-radius"],
        height: "1.9rem",
    },
};

export const outlinedButtonStyles = {
    "&.MuiButton-root": {
        ...whiteButtonStyles["&.MuiButton-root"],
        border: "1px solid black",
    },
};

const outlinedButtonDisabledStyles = {
    "&.MuiButton-root": {
        ...whiteButtonStyles["&.MuiButton-root"],
        border: "#00000040",
    },
};

export const dangerButtonStyles = {
    "&.MuiButton-root": {
        color: "#9B5067",
        backgroundColor: "#FCEFF3",
        border: `0`,
        borderRadius: variables["border-radius"],
        height: "1.9rem",
        "&:hover": {
            backgroundColor: "#E9DDE1",
        },
    },
};

export const infoButtonStyles = {
    "&.MuiButton-root": {
        color: variables["info"],
        backgroundColor: "transparent",
        border: `1px solid ${variables["info"]}2E`,
        borderRadius: variables["border-radius"],
        height: "1.9rem",
        "&:hover": {
            backgroundColor: variables["info-light"],
        },
    },
};

export const purpleButtonStyles = {
    "&.MuiButton-root": {
        color: variables["brand"],
        backgroundColor: "transparent",
        border: `1px solid ${variables["brand"]}2E`,
        borderRadius: variables["border-radius"],
        height: "1.9rem",
        "&:hover": {
            backgroundColor: "#F6F4FC",
        },
    },
};

const buttonTypeStylesMap: Record<string, SxProps<Theme>> = {
    primary: blackButtonStyles,
    secondary: whiteButtonStyles,
    danger: dangerButtonStyles,
    info: infoButtonStyles,
    purple: purpleButtonStyles,
};

function getButtonTypeStyles(props: { buttonType?: StyledButtonType; disabled?: boolean }): SxProps<Theme> {
    if (props.buttonType === "outlined") {
        return props.disabled ? outlinedButtonDisabledStyles : outlinedButtonStyles;
    }
    return (props.buttonType && buttonTypeStylesMap[props.buttonType]) || {};
}

export const mergeSx = (...sxProps: SxProps<Theme>[]): SxProps<Theme> => {
    return sxProps.reduce((prev, currentValue) => {
        return [
            ...(Array.isArray(prev) ? prev : [prev]),
            ...(Array.isArray(currentValue) ? currentValue : [currentValue]),
        ];
    }, [] as SxProps<Theme>);
};

export const withSxProp = (sxProp: SxProps<Theme> | undefined, other: SxProps<Theme>): SxProps<Theme> => {
    if (!sxProp) {
        return other;
    } else {
        return mergeSx(sxProp, other);
    }
};
